import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Image, Upload, Loader, AlertCircle, Chrome, FileText, X } from 'lucide-react';
import { analyzeImageWithGPT4, uploadImage, analyzeTextWithGPT4 } from '../services/imageService';
import { useAppContext } from '../hooks/useAppContext';
import { useSupabaseUser } from '../hooks/useSupabaseUser';
import { ImageAnalysis, AnalysisStyle } from '../types';
import PromptSelector from './PromptSelector';
import ProgressSteps, { ProgressStep } from './ProgressSteps';
import { supabase } from '../lib/supabase';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

function getSteps(type: 'image' | 'text'): ProgressStep[] {
  if (type === 'image') {
    return [
      { id: 'upload', label: 'Uploading Image', status: 'waiting' },
      { id: 'analyze', label: 'Analyzing Content', status: 'waiting' },
      { id: 'complete', label: 'Generating Insights', status: 'waiting' },
    ];
  }
  return [
    { id: 'analyze', label: 'Analyzing Text', status: 'waiting' },
    { id: 'process', label: 'Processing Content', status: 'waiting' },
    { id: 'complete', label: 'Generating Insights', status: 'waiting' },
  ];
}

export default function ImageUploader() {
  const navigate = useNavigate();
  const { setImageAnalysis } = useAppContext();
  const { user } = useSupabaseUser();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [analysisStyle, setAnalysisStyle] = useState<AnalysisStyle>('skeptic');
  const [steps, setSteps] = useState<ProgressStep[]>(getSteps('image'));
  const [isProfileComplete, setIsProfileComplete] = useState(true); 
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [inputText, setInputText] = useState('');
  const [inputType, setInputType] = useState<'image' | 'text'>('image');
  const [showExtensionBanner, setShowExtensionBanner] = useState(true);

  useEffect(() => {
    async function checkProfile() {
      if (!user?.id) {
        setIsCheckingProfile(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;

        // If no profile found or incomplete
        const isComplete = data && !!(data.name && data.role && data.company_description);
        if (!isComplete) {
          setError('Please complete your profile before uploading images');
          setIsProfileComplete(false);
        } else {
          setError(null);
          setIsProfileComplete(true);
        }
      } catch (err) {
        console.error('Error checking profile:', err);
        setIsProfileComplete(false);
        setError('Error checking profile status');
      } finally {
        setIsCheckingProfile(false);
      }
    }

    checkProfile();
  }, [user]);

  useEffect(() => {
    setSteps(getSteps(inputType));
  }, [inputType]);

  const updateStepStatus = (stepId: string, status: ProgressStep['status']) => {
    setSteps(currentSteps =>
      currentSteps.map(step =>
        step.id === stepId ? { ...step, status } : step
      )
    );
  };

  const resetSteps = () => {
    setSteps(getSteps(inputType));
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!isProfileComplete) {
      setError('Please complete your profile before uploading images');
      navigate('/profile');
      return;
    }

    if (acceptedFiles.length === 0) {
      setError('Please select an image file');
      return;
    }
    
    const file = acceptedFiles[0];
    setSelectedFile(file);
    setError(null);
  }, [isProfileComplete, navigate]);

  const handleAnalyze = async () => {
    if (!isProfileComplete) {
      setError('Please complete your profile before analyzing content');
      return;
    }

    if (!selectedFile && !inputText || !user) {
      setError('Please provide content to analyze and ensure you are signed in');
      return;
    }

    setIsUploading(true);
    setError(null);
    resetSteps();

    try {
      if (inputType === 'image') {
        // Existing image analysis logic
        if (!selectedFile.type.startsWith('image/')) {
          throw new Error('Please upload an image file');
        }

        updateStepStatus('upload', 'in_progress');
        const uploadResult = await uploadImage(selectedFile, user.id);
        updateStepStatus('upload', 'completed');
        
        if (!uploadResult.imageUrl) {
          throw new Error('Failed to upload image');
        }

        updateStepStatus('analyze', 'in_progress');
        const analysis = await analyzeImageWithGPT4(uploadResult.imageUrl, analysisStyle);
        updateStepStatus('analyze', 'completed');

        updateStepStatus('complete', 'in_progress');
        setImageAnalysis({
          ...analysis,
          imageUrl: uploadResult.imageUrl
        });
        updateStepStatus('complete', 'completed');
      } else {
        // Text analysis logic
        updateStepStatus('analyze', 'in_progress');
        const analysis = await analyzeTextWithGPT4(inputText, analysisStyle);
        updateStepStatus('analyze', 'completed');

        updateStepStatus('process', 'in_progress');
        // Processing step
        updateStepStatus('process', 'completed');

        updateStepStatus('complete', 'in_progress');
        setImageAnalysis({
          ...analysis,
          text: inputText
        });
        updateStepStatus('complete', 'completed');
      }
      
      navigate('/questions');
    } catch (err) {
      console.error('Error processing content:', err);
      setError(err instanceof Error ? err.message : 'Failed to process content');
      const currentStep = steps.find(step => step.status === 'in_progress');
      if (currentStep) {
        updateStepStatus(currentStep.id, 'error');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxFiles: 1
  });

  if (isCheckingProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Chrome Extension Banner */}
      {showExtensionBanner && (
        <div className="mb-8 relative rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Chrome className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1 md:flex md:justify-between md:items-center">
                <p className="text-sm text-blue-700">
                  Want to create posts directly in LinkedIn? Install our Chrome extension!
                </p>
                <div className="mt-3 md:mt-0 md:ml-6">
                  <a
                    href="https://chromewebstore.google.com/detail/postnudge-voice-to-linked/cnebnhncpobjmlegjlfhlglmamflnlko"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    Install Extension
                  </a>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="flex rounded-md p-1.5 hover:bg-blue-100"
              onClick={() => setShowExtensionBanner(false)}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5 text-blue-600" />
            </button>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Create Your Content</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Upload a screenshot or paste text you want to talk about
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column: Input Methods */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
          <div className="bg-white/80 rounded-xl p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
                <div className="text-xl font-bold text-blue-600">1</div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Upload Content</h2>
                <p className="text-sm text-gray-600">Share something you want to talk about</p>
              </div>
            </div>

            {/* Move Tabs inside first column */}
            <Tabs defaultValue="image" onValueChange={(value) => setInputType(value as 'image' | 'text')}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="image" className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Upload Image
                </TabsTrigger>
                <TabsTrigger value="text" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Paste Text
                </TabsTrigger>
              </TabsList>

              <TabsContent value="image">
                {isUploading && <ProgressSteps steps={steps} />}
                {/* Existing image upload UI */}
                <div
                  {...getRootProps()}
                  className={`
                    relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
                    transition-all duration-200 ease-in-out
                    ${isDragActive 
                      ? 'border-blue-400 bg-blue-50/50' 
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50/50'
                    }
                  `}
                >
                  <input {...getInputProps()} />
                  
                  {selectedFile ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="bg-white/80 rounded-lg p-2 shadow-sm">
                          <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="Selected"
                            className="max-h-48 rounded-lg"
                          />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{selectedFile.name}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
                        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                      </div>
                      <p className="text-gray-600">Processing your image...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-100 flex items-center justify-center">
                        <Upload className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900">
                          {isDragActive ? 'Drop your image here' : 'Drag and drop your image here'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          or click to browse
                        </p>
                      </div>
                      <div className="text-xs text-gray-500">
                        Supported formats: PNG, JPG, JPEG
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="text">
                {isUploading && <ProgressSteps steps={steps} />}
                <Textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Paste your content here..."
                  className="min-h-[200px] resize-y"
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right Column: Analysis Style - Already correct */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
          <div className="bg-white/80 rounded-xl p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
                <div className="text-xl font-bold text-blue-600">2</div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Choose Style</h2>
                <p className="text-sm text-gray-600">Select how you want to analyze the post</p>
              </div>
            </div>

            <PromptSelector
              selectedStyle={analysisStyle}
              onStyleSelect={setAnalysisStyle}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center space-y-4">
        <button
          onClick={handleAnalyze}
          disabled={!isProfileComplete || (!selectedFile && !inputText) || isUploading}
          className={`inline-flex items-center px-8 py-3 rounded-lg text-lg font-medium transition-all duration-200 ${
            isProfileComplete && (selectedFile || inputText) && !isUploading
              ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm'
              : 'bg-gray-100 text-gray-400 border border-gray-200'
          }`}
        >
          {isUploading ? (
            <>
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            'Analyze Content'
          )}
        </button>

        {!selectedFile && !inputText && isProfileComplete && (
          <p className="mt-2 text-sm text-gray-500">Please upload an image or paste text</p>
        )}

        {!isProfileComplete && (
          <div className="max-w-md mx-auto">
            <div className="px-4 py-2.5 bg-red-50 border border-red-100 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">Complete your profile to analyze posts</p>
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="ml-3 inline-flex items-center px-2.5 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors duration-200"
              >
                Complete →
              </button>
            </div>
          </div>
        )}

        {error && isProfileComplete && (
          <div className="max-w-md mx-auto">
            <div className="px-4 py-2.5 bg-red-50 border border-red-100 rounded-lg flex items-center">
              <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}